import { cn } from '@/lib/utils';
import { EM_DASH } from './evidencePrimitives';

interface CellValueProps {
  value: string | null | undefined;
  className?: string;
}

/**
 * A single evidence value.
 *
 * The client does no arithmetic, appends no unit and formats no number — the server sends finished strings.
 * The ONLY decision made here is the absent one: null, undefined, a blank string, and anything that is not a
 * string at all render as an em-dash, muted, with an `sr-only` word beside it. A screen reader announces
 * "—" as nothing, and an audit trail that silently drops a value is worse than useless.
 *
 * ⚠️ The guard is `typeof`, not a null check. Every array input in this folder is hardened with
 * `Array.isArray` because the wire is untrusted; a non-string cell would have thrown on `.trim()` and
 * escaped `EvidenceBlock`'s promise never to throw — children render after the parent returns, so no
 * try/catch up there could have caught it.
 */
export function CellValue({ value, className }: CellValueProps) {
  if (typeof value !== 'string' || value.trim() === '') {
    // Caller classes come FIRST so `text-muted-foreground` wins the tailwind-merge: a caller styling the
    // value `text-foreground` must not repaint the absent marker as if it were a value.
    return (
      <span className={cn(className, 'text-muted-foreground')}>
        <span aria-hidden>{EM_DASH}</span>
        <span className="sr-only">None</span>
      </span>
    );
  }

  return <span className={className}>{value}</span>;
}
